import { Component, OnDestroy, OnInit, computed, inject, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { BedStatesService } from '../../../core/services/bed-states.service';
import { IBedState, compareBedStates } from '../../models/be/bed-state';
import { BedPostureIconComponent, formatBedDuration, posturePresentation } from './bed-posture-icon.component';
import { BedStateHistoryDialogComponent } from './bed-state-history-dialog.component';

@Component({
  selector: 'app-bed-states-room',
  imports: [TranslateModule, BedPostureIconComponent],
  template: `
    @if (store.canView && !store.denied()) {
      <div class="bed-states" [class.detailed]="detailed()" [class.symbols]="!detailed()">
        @if (detailed()) { <h3>{{'BED_STATES.TITLE' | translate}}</h3> }
        @for (bed of displayedBeds(); track bed.logicalBedId) {
          <div class="bed-state-row" [class]="'bed-state-row ' + presentation(bed.state).css"
            [attr.title]="!detailed() ? bed.deviceLabel + ' · ' + (presentation(bed.state).label | translate) : null">
            <app-bed-posture-icon [state]="bed.state" />
            <div class="state-copy">
              @if (!detailed()) {
                <span class="visually-hidden">{{bed.deviceLabel}} · {{presentation(bed.state).label | translate}}</span>
              } @else {
                <span class="sensor-label" [title]="bed.deviceLabel">{{bed.deviceLabel}}</span>
                <strong>{{presentation(bed.state).label | translate}}</strong>
              }
              @if (bed.stateSince) {
                <span class="duration">{{(bed.state === 'OUT_OF_BED' ? 'BED_STATES.OUT_SINCE' : 'BED_STATES.SINCE') | translate: { duration: duration(bed) } }}</span>
              }
            </div>
            @if (detailed()) {
              <button type="button" (click)="openHistory(bed, $event)"
                [attr.aria-label]="('BED_STATES.HISTORY_FOR' | translate: { sensor: bed.deviceLabel })">
                {{'BED_STATES.HISTORY' | translate}}
              </button>
            }
          </div>
        } @empty {
          @if (detailed()) {
            <span class="empty-state">{{(store.loading() ? 'BED_STATES.LOADING' : 'BED_STATES.NO_DATA') | translate}}</span>
          } @else {
            <div class="bed-state-row unknown">
              <app-bed-posture-icon [state]="null" />
              <span class="visually-hidden">{{(store.loading() ? 'BED_STATES.LOADING' : 'BED_STATES.NO_DATA') | translate}}</span>
            </div>
          }
        }
      </div>
    }
  `,
  styleUrl: './bed-states-room.component.scss'
})
export class BedStatesRoomComponent implements OnInit, OnDestroy {
  readonly store = inject(BedStatesService);
  private readonly dialog = inject(MatDialog);
  readonly roomId = input.required<string>();
  readonly unitId = input('');
  readonly detailed = input(false);
  readonly beds = computed(() => [...this.store.states().values()]
    .filter(bed => bed.roomId === this.roomId() && (!this.unitId() || bed.unitId === this.unitId()))
    .sort((a, b) => a.deviceLabel.localeCompare(b.deviceLabel)));
  // Cards summarize the latest sensor observation; room details retain every sensor.
  readonly displayedBeds = computed(() => this.detailed() ? this.beds()
    : [...this.beds()].sort((a, b) => compareBedStates(b, a)).slice(0, 1));
  readonly presentation = posturePresentation;
  private release?: () => void;

  ngOnInit(): void { this.release = this.store.acquire(); }
  ngOnDestroy(): void { this.release?.(); }
  duration(bed: IBedState): string {
    const seconds = (this.store.now() - Date.parse(bed.stateSince!)) / 1000;
    if (bed.state === 'OUT_OF_BED') {
      const minutes = Math.max(0, Math.floor(seconds / 60));
      return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    }
    return formatBedDuration(seconds);
  }
  openHistory(bed: IBedState, event: Event): void {
    event.stopPropagation();
    if (!this.store.canView || this.store.denied()) return;
    this.dialog.open(BedStateHistoryDialogComponent, { data: bed, width: '760px', maxWidth: '96vw', maxHeight: '90vh' });
  }
}
